import {Dialog} from 'platform/components';
import {Show} from 'platform/foundation';

import {useEffect, useRef, useState} from 'react';

import {head, isNil, isNotNil} from 'ramda';

import {
  GetDirectSaleLabourItemResponse,
  GetDirectSaleMaterialItemResponse,
  useLazyGetDirectSaleLabourItemQuery,
  useLazyGetDirectSaleMaterialItemQuery,
  usePatchDirectSaleLabourItemMutation,
  usePatchDirectSaleMaterialItemMutation,
} from '@dms/api/warehouse';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ControlledRowControl, RowV5ResponseBody} from 'features/datagrid';

import {RequestTabLabourItemEdit} from './RequestTabLabourItemEdit';
import {RequestTabMaterialItemEdit} from './RequestTabMaterialItemEdit';

export type Item = GetDirectSaleMaterialItemResponse | GetDirectSaleLabourItemResponse;

export type RequestTabEditProps = {
  directSaleId: string;
  itemId: string;
  isEditingDisabled?: boolean;
  authorizationProfileId?: string;
  dataQueryId: string;
  onClose: () => void;
} & RequiredTestIdProps;

export function RequestTabEdit(props: RequestTabEditProps) {
  const [item, setItem] = useState<Item | null>(null);

  const [getDirectSaleMaterialItem] = useLazyGetDirectSaleMaterialItemQuery();
  const [getDirectSaleLabourItem] = useLazyGetDirectSaleLabourItemQuery();

  const [, patchMaterialItemStatus] = usePatchDirectSaleMaterialItemMutation({
    fixedCacheKey: 'edit-direct-sale-material-item',
  });

  const [, patchLabourItemStatus] = usePatchDirectSaleLabourItemMutation({
    fixedCacheKey: 'edit-direct-sale-labour-item',
  });

  const isSavedByRowControlRef = useRef<boolean>(false);

  const materialItemFormRef = useRef<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>(null);

  const labourItemFormRef = useRef<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>(null);

  const isSavingChanges = patchMaterialItemStatus.isLoading || patchLabourItemStatus.isLoading;

  const isItemLabour = item && 'workType' in item;
  const isItemMaterial = item && 'manufacturerId' in item;

  const handleGetItemDetail = async (itemId: string) => {
    const directSaleMaterialItemPromise = getDirectSaleMaterialItem({
      directSaleId: props.directSaleId,
      itemId,
    })
      .unwrap()
      .catch(handleApiError);

    const directSaleLabourItemPromise = getDirectSaleLabourItem({
      directSaleId: props.directSaleId,
      itemId,
    })
      .unwrap()
      .catch(handleApiError);

    const responses = await Promise.all([
      directSaleMaterialItemPromise,
      directSaleLabourItemPromise,
    ]);

    const response = head(responses.filter((res) => isNotNil(res)));

    setItem(response as Item);
  };

  useEffect(() => {
    if (isNil(props.itemId)) {
      return;
    }
    handleGetItemDetail(props.itemId);
    // Disabled exhaustive-deps because handleGetItemDetail only depends on stable values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.itemId]);

  const itemName = item ? `${item?.name} (${item?.number})` : '';

  const handlePageChange = async (row: RowV5ResponseBody) => {
    const isDirty = isItemMaterial
      ? materialItemFormRef.current?.isDirty
      : labourItemFormRef.current?.isDirty;

    const submitForm = isItemMaterial
      ? materialItemFormRef.current?.submit
      : labourItemFormRef.current?.submit;

    // No changes done, continue to next item.
    if (!isDirty) {
      handleGetItemDetail(row.id);
      return true;
    }

    isSavedByRowControlRef.current = true;

    const isSubmitSuccessfull = await submitForm?.();

    const hasApiError = isItemMaterial
      ? patchMaterialItemStatus.isError
      : patchLabourItemStatus.isError;

    // Validation failed or API error occured, stay on current item.
    if (!isSubmitSuccessfull || hasApiError) {
      isSavedByRowControlRef.current = false;
      return false;
    }

    // Intentionally not awaited — pagination advances immediately after save,
    // while the next item fetches in the background.
    handleGetItemDetail(row.id);
    return true;
  };

  return (
    <>
      <Dialog
        title={itemName}
        isOpen={isNotNil(props.itemId)}
        scrollBehavior="inside"
        withAdditionalFooter
        onClose={props.onClose}
        pagination={
          <Show when={isNotNil(props.dataQueryId) && isNotNil(item)}>
            <ControlledRowControl
              isDisabled={isSavingChanges}
              dataQueryId={props.dataQueryId as string}
              gridCode="direct-sale-item"
              entityId={item?.id as string}
              onPageChange={handlePageChange}
            />
          </Show>
        }
        data-testid={props['data-testid']}
      >
        <Show when={isItemMaterial}>
          <RequestTabMaterialItemEdit
            directSaleId={props.directSaleId}
            item={item as GetDirectSaleMaterialItemResponse}
            isEditingDisabled={props.isEditingDisabled}
            authorizationProfileId={props.authorizationProfileId}
            isSavedByRowControlRef={isSavedByRowControlRef}
            formRef={materialItemFormRef}
            onClose={props.onClose}
            data-testid={suffixTestId('dialog.editMaterial', props)}
          />
        </Show>

        <Show when={isItemLabour}>
          <RequestTabLabourItemEdit
            directSaleId={props.directSaleId}
            item={item as GetDirectSaleLabourItemResponse}
            isEditingDisabled={props.isEditingDisabled}
            authorizationProfileId={props.authorizationProfileId}
            isSavedByRowControlRef={isSavedByRowControlRef}
            formRef={labourItemFormRef}
            onClose={props.onClose}
            data-testid={suffixTestId('dialog.editLabour', props)}
          />
        </Show>
      </Dialog>
    </>
  );
}
